import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { doc, writeBatch, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

// Helper: Deep Equal che ignora valori undefined
function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;

    if (obj1 && typeof obj1.toDate === 'function' && obj2 && typeof obj2.toDate === 'function') {
        return obj1.isEqual(obj2);
    }
    
    if (obj1 instanceof Date && obj2 instanceof Date) {
        return obj1.getTime() === obj2.getTime();
    }

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const getValidKeys = (obj) => Object.keys(obj).filter(key => obj[key] !== undefined);

    const keys1 = getValidKeys(obj1);
    const keys2 = getValidKeys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

// Helper: Deep Clone pulito
function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }
    if (obj.toDate && typeof obj.toDate === 'function') {
        return obj.toDate();
    }
    if (Array.isArray(obj)) {
        return obj.map(item => deepClone(item));
    }
    
    const objCopy = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (value !== undefined) {
                 objCopy[key] = deepClone(value);
            }
        }
    }
    return objCopy;
}

export const useMoodboardHistory = (project, projectId, canAutosave) => {
    const [history, setHistory] = useState([{}]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('Initializing...');
    
    // 1. ADDED: Versione per forzare il ricalcolo dopo il salvataggio
    const [saveVersion, setSaveVersion] = useState(0);
    
    const isHistoryInitialized = useRef(false);
    const lastSavedState = useRef(null);

    // Initial state loading
    useEffect(() => {
        if (project && !isHistoryInitialized.current) {
            const processInitialState = async () => {
                let initialState = {};
                
                try {
                    const plansCollectionRef = collection(db, 'projects', projectId, 'moodboard_plans');
                    const plansSnapshot = await getDocs(plansCollectionRef);
                    
                    if (!plansSnapshot.empty) {
                        plansSnapshot.forEach(docSnap => {
                            initialState[docSnap.id] = docSnap.data().entities || [];
                        });
                    } else if (project.moodboardState && Object.keys(project.moodboardState).length > 0) {
                        initialState = project.moodboardState;
                    }
                } catch (fetchError) {
                    console.error("Failed to fetch moodboard state:", fetchError);
                }
                
                const finalState = deepClone(initialState || {});

                setHistory([finalState]);
                setHistoryIndex(0);
                lastSavedState.current = finalState;
                isHistoryInitialized.current = true;
                setSaveStatus('Saved');
            };
            
            processInitialState();
        }
    }, [project, projectId]);

    const currentState = history[historyIndex] || {};

    // 2. FIXED: Dipendenza aggiunta (saveVersion). 
    // Ora quando salviamo, questo ricalcola e vede che "currentState" è uguale a "lastSavedState"
    const hasUnsavedChanges = useMemo(() => {
        if (!isHistoryInitialized.current) return false;
        return !deepEqual(currentState, lastSavedState.current);
    }, [currentState, saveVersion]); 

    // Visual Status Indicator
    useEffect(() => {
        if (!isHistoryInitialized.current) return;
        
        if (isSaving) {
            setSaveStatus('Saving...');
        } else if (hasUnsavedChanges) {
            setSaveStatus('Draft');
        } else {
            setSaveStatus('Saved');
        }
    }, [hasUnsavedChanges, isSaving]);


    const updateHistory = useCallback((newState) => {
        if (!isHistoryInitialized.current) return;
        
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(newState);
            return newHistory;
        });
        setHistoryIndex(prev => prev + 1);
    }, [historyIndex]);

    const undo = useCallback(() => {
        setHistoryIndex(prev => (prev > 0 ? prev - 1 : prev));
    }, []);

    const redo = useCallback(() => {
        setHistoryIndex(prev => (prev < history.length - 1 ? prev + 1 : prev));
    }, [history.length]);

    const currentStateRef = useRef(currentState);
    useEffect(() => { currentStateRef.current = currentState; }, [currentState]);

    const handleSave = useCallback(async () => {
        const stateToSave = currentStateRef.current;
        // Doppio controllo di sicurezza prima della chiamata di rete
        const areDifferent = !deepEqual(stateToSave, lastSavedState.current);

        if (!areDifferent) {
            setIsSaving(false); 
            // Se sono uguali, forziamo comunque l'aggiornamento UI su Saved
            setSaveVersion(v => v + 1); 
            return;
        }

        setIsSaving(true);
        setSaveStatus('Saving...');
        
        try {
            const batch = writeBatch(db);
            const plansCollectionRef = collection(db, 'projects', projectId, 'moodboard_plans');
            
            const existingPlansSnapshot = await getDocs(plansCollectionRef);
            const existingPlanIds = new Set(existingPlansSnapshot.docs.map(d => d.id));
            const currentPlanIds = new Set(Object.keys(stateToSave));

            for (const planId of currentPlanIds) {
                const planDocRef = doc(plansCollectionRef, planId);
                const cleanEntities = deepClone(stateToSave[planId] || []); 
                batch.set(planDocRef, { entities: cleanEntities });
            }

            existingPlanIds.forEach(id => {
                if (!currentPlanIds.has(id)) {
                    batch.delete(doc(plansCollectionRef, id));
                }
            });
            
            await batch.commit();

            lastSavedState.current = deepClone(stateToSave);
            
            // 3. CRITICAL: Aggiorniamo la versione per notificare useMemo che il salvataggio è avvenuto
            setSaveVersion(v => v + 1);
            setSaveStatus('Saved');

        } catch (error) {
            console.error("Failed to save moodboard:", error);
            setSaveStatus('Error!');
        } finally {
            setIsSaving(false);
        }
    }, [projectId]);

    const saveRef = useRef(handleSave);
    useEffect(() => { saveRef.current = handleSave; }, [handleSave]);

    useEffect(() => {
        if (!canAutosave || !isHistoryInitialized.current || !hasUnsavedChanges || isSaving) {
            return;
        }

        const handler = setTimeout(() => {
            saveRef.current();
        }, 3000);

        return () => clearTimeout(handler);
    }, [hasUnsavedChanges, canAutosave, isSaving]); 

    return {
        currentPlanStates: currentState,
        updateHistory,
        undo,
        redo,
        canUndo: historyIndex > 0,
        canRedo: historyIndex < history.length - 1,
        saveStatus,
        isSaving,
        handleSave,
        hasUnsavedChanges,
        isHistoryInitialized: isHistoryInitialized.current
    };
};